import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  FileEvent,
  FileEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/files/v1/file_events_pb';
import { Col } from '@rematter/ui-web';
import { EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { FileAccessedDetail } from './FileAccessedDetail';
import { FileCreatedDetail } from './FileCreatedDetail';
import { FileDeletedDetail } from './FileDeletedDetail';
import { FileMovedDetail } from './FileMovedDetail';
import { FilePermissionChangedDetail } from './FilePermissionChangedDetail';
import { FileQuotaExceededDetail } from './FileQuotaExceededDetail';
import { FileRemovedFromTemplateDetail } from './FileRemovedFromTemplateDetail';
import { FileSetAsTemplateDetail } from './FileSetAsTemplateDetail';

type FileEventDetailProps = {
  payload: string;
};

export const FileEventDetail = ({ payload }: FileEventDetailProps) => {
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): FileEvent | null => {
    try {
      return fromJson(FileEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title="Raw Payload (Parse Error)" />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <FileCreatedDetail event={inner.value} />;
      case 'accessed':
        return <FileAccessedDetail event={inner.value} />;
      case 'deleted':
        return <FileDeletedDetail event={inner.value} />;
      case 'quotaExceeded':
        return <FileQuotaExceededDetail event={inner.value} />;
      case 'moved':
        return <FileMovedDetail event={inner.value} />;
      case 'permissionChanged':
        return <FilePermissionChangedDetail event={inner.value} />;
      case 'setAsTemplate':
        return <FileSetAsTemplateDetail event={inner.value} />;
      case 'removedFromTemplate':
        return <FileRemovedFromTemplateDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title="Unknown Event Type" />;
    }
  };

  const getEventTitle = (): string => {
    switch (inner.case) {
      case 'created':
        return 'File Created';
      case 'accessed':
        return 'File Accessed';
      case 'deleted':
        return 'File Deleted';
      case 'quotaExceeded':
        return 'File Quota Exceeded';
      case 'moved':
        return 'File Moved';
      case 'permissionChanged':
        return 'File Permission Changed';
      case 'setAsTemplate':
        return 'File Set As Template';
      case 'removedFromTemplate':
        return 'File Removed From Template';
      case undefined:
      default:
        return 'Unknown Event';
    }
  };

  return (
    <Col className="@FileEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
